"use client";

import * as React from 'react';
import type { TooltipProps } from 'recharts';
import { Tooltip } from 'recharts';

import { cn } from '../../lib/utils';

export type ChartConfig = Record<
  string,
  {
    label?: string;
    color?: string;
  }
>;

interface ChartContextValue {
  config: ChartConfig;
}

const ChartContext = React.createContext<ChartContextValue | null>(null);

interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config: ChartConfig;
}

export function ChartContainer({ className, config, children, ...props }: ChartContainerProps) {
  return (
    <ChartContext.Provider value={{ config }}>
      <div
        className={cn(
          'flex w-full flex-1 rounded-lg border border-border bg-card p-4 shadow-none',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </ChartContext.Provider>
  );
}

export function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a ChartContainer');
  }

  return context;
}

type ChartTooltipProps = TooltipProps<number, string>;

export function ChartTooltip({ content, ...props }: ChartTooltipProps) {
  return (
    <Tooltip
      {...props}
      content={content}
      cursor={{ fill: 'hsl(var(--muted) / 0.6)' }}
      wrapperStyle={{ outline: 'none' }}
    />
  );
}

interface ChartTooltipContentProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color?: string;
    fill?: string;
  }>;
  label?: string;
}

export function ChartTooltipContent({ active, payload, label }: ChartTooltipContentProps) {
  const { config } = useChart();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="grid gap-1 rounded-lg border border-border bg-background px-3 py-2 text-[11px] shadow-[var(--shadow-card)]">
      {label ? <span className="font-semibold text-foreground">{label}</span> : null}
      {payload.map((item) => {
        const itemConfig = config[item.name];
        const swatchColor = item.color ?? item.fill ?? itemConfig?.color ?? '#8884d8';

        const entryPayload = 'payload' in item ? (item.payload as { rawValue?: number } | undefined) : undefined;
        const displayValue = Math.round(entryPayload?.rawValue ?? item.value ?? 0);

        return (
          <div key={item.name} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: swatchColor }} />
              <span className="text-muted-foreground">
                {itemConfig?.label ?? item.name}
              </span>
            </span>
            <span className="font-semibold text-foreground">{displayValue}</span>
          </div>
        );
      })}
    </div>
  );
}
